"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileInput = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const react_dropzone_1 = require("react-dropzone");
const FileInfo_1 = require("./FileInput/FileInfo");
const utils_2 = require("./utils");
const aria_1 = require("./utils/aria");
const Toast_1 = require("../../Toast");
function FileInputElem(_a) {
    var { hasError, onFocus: _onFocus, onBlur: _onBlur, success, ref: _ref, onChange, processFile, value, accept: acceptProp, renderChildren: Render = utils_2.DefaultDropzone, info: InfoElement = FileInfo_1.FileInfo, childrenProps = {}, prefix: _prefix, suffix: _suffix, label, optional } = _a, props = __rest(_a, ["hasError", "onFocus", "onBlur", "success", "ref", "onChange", "processFile", "value", "accept", "renderChildren", "info", "childrenProps", "prefix", "suffix", "label", "optional"]);
    const accept = (0, react_1.useMemo)(() => supportedAccept(acceptProp), [acceptProp]);
    const inputId = (0, app_1.useCuid)();
    const mounted = (0, react_1.useRef)(true);
    const processingOverride = (0, react_1.useRef)();
    const [processing, setProcessing] = (0, react_1.useState)([]);
    const { maxFiles = Infinity, maxSize = Infinity } = childrenProps;
    const handleFiles = (0, react_1.useCallback)((files) => __awaiter(this, void 0, void 0, function* () {
        const allFiles = [...value, ...processing];
        const overrideSingleFile = maxFiles === 1 && files.length === 1;
        if (!overrideSingleFile && allFiles.length + files.length > maxFiles) {
            Toast_1.toast.error('Too many files provided');
            return;
        }
        let duplicateFiles = 0;
        let filteredFiles = files.filter((file) => {
            if (allFiles.some((curr) => curr.name === file.name)) {
                duplicateFiles += 1;
                return false;
            }
            return true;
        });
        if (duplicateFiles) {
            Toast_1.toast.warn(`Skipping ${duplicateFiles} duplicate files.`);
        }
        if (filteredFiles.length > 0) {
            const id = (0, utils_1.cuid)();
            if (overrideSingleFile) {
                processingOverride.current = id;
                onChange([]);
                setProcessing([]);
            }
            if (processFile) {
                const toProcess = filteredFiles.map((file) => processFile(file));
                setProcessing((current) => [...current, ...filteredFiles]);
                const rawFiles = filteredFiles;
                filteredFiles = yield Promise.all(toProcess);
                setProcessing((current) => current.filter((file) => !rawFiles.includes(file)));
            }
            const abort = overrideSingleFile && processingOverride.current !== id;
            if (mounted.current && !abort) {
                onChange(overrideSingleFile
                    ? filteredFiles
                    : (current) => [...current, ...filteredFiles]);
            }
        }
    }), [processFile, onChange, value, maxFiles, processing]);
    const { getRootProps, getInputProps, isDragActive, open } = (0, react_dropzone_1.useDropzone)({
        accept,
        onDropRejected: (rejections) => {
            rejections.forEach((rejection) => {
                const [{ code }] = rejection.errors;
                const { file } = rejection;
                switch (code) {
                    case 'file-too-large': {
                        Toast_1.toast.error(`File: ${file.name}  too large.`);
                        break;
                    }
                    case 'too-many-files': {
                        Toast_1.toast.warn('Too many files provided.');
                        break;
                    }
                    case 'file-invalid-type': {
                        Toast_1.toast.error(`Invalid file type: ${file.type}.`);
                        break;
                    }
                    default: {
                        Toast_1.toast.error(`Invalid file: ${file.name}`);
                    }
                }
            });
        },
        multiple: maxFiles !== 1,
        maxFiles,
        maxSize,
        onDrop: handleFiles,
        noClick: true,
    });
    (0, react_1.useEffect)(() => {
        return () => {
            mounted.current = false;
        };
    }, []);
    const handleAdd = (0, react_1.useCallback)((ev) => {
        ev.stopPropagation();
        open();
    }, [open]);
    const handleRemove = (0, react_1.useCallback)((fileToRemove) => onChange((curr) => curr.filter((file) => file.name !== fileToRemove.name)), [onChange]);
    const canAdd = (0, react_1.useMemo)(() => value.length < maxFiles, [maxFiles, value.length]);
    const acceptedFormats = (0, react_1.useMemo)(() => [
        ...new Set(Object.values(accept !== null && accept !== void 0 ? accept : {})
            .flat()
            .map((v) => (v === '.jpeg' ? '.jpg' : v))),
    ], [accept]);
    return ((0, jsx_runtime_1.jsx)(Render, Object.assign({ addFile: handleAdd, removeFile: handleRemove, wrapperProps: getRootProps((0, aria_1.aria)({ invalid: hasError, success })), dragging: isDragActive, processing: processing.length, files: value, canAdd: canAdd, id: inputId, label: label, optional: optional, setFiles: onChange }, childrenProps, { children: canAdd ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("input", Object.assign({ id: inputId }, getInputProps(props))), (0, jsx_runtime_1.jsx)(InfoElement, { acceptedFormats: acceptedFormats })] })) : null })));
}
function FileInput(_a) {
    var { defaultValue, render, renderProps } = _a, props = __rest(_a, ["defaultValue", "render", "renderProps"]);
    return ((0, jsx_runtime_1.jsx)(utils_2.InputController, Object.assign({}, props, { render: FileInputElem, renderChildren: render, childrenProps: renderProps, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : [] })));
}
exports.FileInput = FileInput;
const SUPPORTED = new Set([...app_1.SUPPORTED_IMAGES, ...app_1.SUPPORTED_FILES]);
const DEFAULT_IMAGES = ['.jpg', '.jpeg', '.png', '.webp', '.svg'];
const DEFAULT_FILES = ['.pdf', '.zip'];
const DEFAULT_ACCEPT = [...DEFAULT_IMAGES.slice(0, 4), ...DEFAULT_FILES];
function supportedAccept(accept = {}) {
    const result = {};
    Object.entries(accept).forEach(([key, value]) => {
        const filtered = value.filter((v) => SUPPORTED.has(v));
        result[key] =
            filtered.length > 0
                ? filtered
                : key === 'image/*'
                    ? DEFAULT_IMAGES
                    : DEFAULT_FILES;
    });
    if ((0, utils_1.keys)(result).length === 0) {
        result['*'] = DEFAULT_ACCEPT;
    }
    return result;
}
